import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { UpdateUserRoleByAdmin } from "../auth";
import { ROLE_CAPACITY_CONFIG, computeRoleHash } from "./roleUser";
import { Family } from "./Rolle";
import Timer from "./timer";
import ErrorAlert from "./ErrorAlert";
import "./EditRoleCapacityModal.css";

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["editRoleCapacityModal"]?.[key] || fallback;

// Same team-managing fields as addAdminFamily.jsx's formPassWord(), gated by
// the exact same role-level thresholds (roleI >= 4/5/6/7) -- reproduced here
// rather than imported since addAdminFamily's version is inline JSX tied to
// its own local state/handlers. Each entry's `key` matches RoleSchema's real
// field name (never renamed).
const CAPACITY_FIELDS = [
  { key: "maxRoyalite", minLevel: 4, labelKey: "t001", fallback: "Unlimited Royalty" },
  { key: "maxSeller", minLevel: 4, labelKey: "t002", fallback: "Max Seller" },
  { key: "maxProducer", minLevel: 4, labelKey: "t003", fallback: "Max Producer" },
  { key: "maxAssociate", minLevel: 5, labelKey: "t004", fallback: "Max Associate" },
  { key: "maxInvestment", minLevel: 6, labelKey: "t005", fallback: "Max Investment" },
  { key: "maxAdmin", minLevel: 7, labelKey: "t006", fallback: "Max Admin" },
];

// Labels for the "promote to a superior role" picker -- same 7 roles/order
// as addAdminFamily.jsx's formSelect() and SuspendUserModal.jsx's
// ROLE_OPTIONS. Kept here (not a shared table) since this is the only place
// that needs a role NAME -> translated LABEL lookup; the level/code table
// itself already lives in roleUser.js's ROLE_CAPACITY_CONFIG.
const ROLE_LABELS = {
  Royalty: { key: "t023", fallback: "User Royal" },
  Seller: { key: "t024", fallback: "Seller" },
  Provider: { key: "t025", fallback: "Provider" },
  Associate: { key: "t026", fallback: "Associate" },
  Investment: { key: "t027", fallback: "Investor" },
  Administrator: { key: "t028", fallback: "Administrator" },
  Family: { key: "t029", fallback: "Developer" },
};

const EMPTY_TEL = { country: "", abrCountry: "", ariaCode: "", National: "", International: "" };

const DEFAULT_LIMITS = {
  maxRoyalite: "",
  maxSeller: "",
  maxProducer: "",
  maxAssociate: "",
  maxInvestment: "",
  maxAdmin: "",
};

// Edits the team-managing limits (max Royalty/Seller/Producer/Associate/
// Investment/Admin) + expiration days of a user CardUser already knows about
// -- the same fields addAdminFamily.jsx's "edit an existing account" branch
// exposes (formPassWord), just entered directly from the target's own card
// instead of re-searching for them by email. Submitting always goes through
// UpdateUserRoleByAdmin with emailExit:false -- per the backend's existing,
// already-tested contract this never mutates the target's live role in
// place; it writes a new pending proposal (userActiveRole.userActiveRole)
// that CardUserActivationModal's own RoleSummaryCard "previous"/"requested"
// comparison and activation link already render, and that only takes effect
// once the target accepts it via UpdateUserRoleTrue.
const EditRoleCapacityModal = ({
  show,
  onHide,
  infoSource,
  token,
  targetUserId,
  targetFirstName,
  targetLastName,
  targetEmail,
  roleName,
  currentLimits,
  promotableRoles = [],
  onSuccess,
}) => {
  __setElakayInfoSource(infoSource);

  const [nbrDays, setNbrDays] = useState(0);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  // The role actually being submitted -- defaults to the target's current
  // role (edit-limits-only, the original behavior), but the admin can raise
  // it to any role in promotableRoles (a role strictly superior to the
  // target's current one, that the acting admin is themselves allowed to
  // grant -- see cardUser.jsx's canEditRoleTier). Never lowered from here --
  // this modal is additive/promotion only, never a demotion tool.
  const [targetRoleName, setTargetRoleName] = useState(roleName);

  // Reset from the target's real, current role/limits every time this modal
  // is (re)opened for a (possibly different) user -- never leftover state
  // from whichever card was edited previously (mission section 11).
  useEffect(() => {
    if (show) {
      setNbrDays(0);
      setTargetRoleName(roleName);
      setLimits({
        maxRoyalite: currentLimits?.maxRoyalite > 0 ? currentLimits.maxRoyalite : "",
        maxSeller: currentLimits?.maxSeller > 0 ? currentLimits.maxSeller : "",
        maxProducer: currentLimits?.maxProducer > 0 ? currentLimits.maxProducer : "",
        maxAssociate: currentLimits?.maxAssociate > 0 ? currentLimits.maxAssociate : "",
        maxInvestment: currentLimits?.maxInvestment > 0 ? currentLimits.maxInvestment : "",
        maxAdmin: currentLimits?.maxAdmin > 0 ? currentLimits.maxAdmin : "",
      });
      setPassword("");
      setFormError(null);
      setFormSuccess(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, targetUserId, roleName]);

  const config = ROLE_CAPACITY_CONFIG[targetRoleName];
  const visibleFields = config ? CAPACITY_FIELDS.filter((f) => config.level >= f.minLevel) : [];
  const isFieldVisible = (key) => visibleFields.some((f) => f.key === key);
  const isPromoting = targetRoleName !== roleName;

  // Same cap-to-the-acting-admin's-own-max rule as addAdminFamily.jsx's
  // handleChangeNumber -- Family() alone gets an unlimited hand (mission:
  // never re-invent this rule, mirror it exactly). 0 is always a valid,
  // final value here (never re-clamped/rejected) -- it's the explicit "not
  // granted" state for a limit that either doesn't apply to this role or
  // that the admin wants to revoke.
  const handleLimitChange = (key) => (event) => {
    const raw = event.target.value;
    if (raw === "") {
      setLimits((prev) => ({ ...prev, [key]: "" }));
      return;
    }
    if (isNaN(raw)) return;
    const numericValue = Number(raw);
    const maxAllowed = infoSource?.userInfo?.userActiveRole?.[key];
    if (numericValue > 0 && !Family() && maxAllowed > 0 && numericValue > maxAllowed) {
      setLimits((prev) => ({ ...prev, [key]: maxAllowed }));
      setFormError(
        __elakayText("t007", "Sorry, you entered a value greater than the maximum allowed, which is") + ` ${maxAllowed}.`
      );
    } else {
      setLimits((prev) => ({ ...prev, [key]: numericValue }));
    }
  };

  // Explicit "reset to zero" affordance -- a visible field can always be
  // cleared back to 0 (e.g. this team member shouldn't be granted this
  // limit after all), independent of handleLimitChange's cap logic.
  const handleResetLimit = (key) => () => setLimits((prev) => ({ ...prev, [key]: 0 }));

  // A visible field only needs a real (non-empty) number -- 0 included --
  // never a > 0 requirement, which used to make it impossible to actually
  // save a limit as "not granted" for this role.
  const isFormValid = Boolean(config)
    && nbrDays > 0
    && password.trim() !== ""
    && visibleFields.every((f) => limits[f.key] !== "" && !isNaN(Number(limits[f.key])));

  const expirationDate = nbrDays > 0 ? Date.now() + nbrDays * 24 * 3600 * 1000 : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!config) {
      setFormError(__elakayText("t008", "This role can't be edited here."));
      return;
    }
    setFormError(null);
    setFormSuccess(null);
    setSubmitting(true);

    // A field not appropriate for this role (hidden -- see visibleFields)
    // is always forced to 0 here, regardless of whatever value it was
    // prefilled with from the target's previous limits -- never silently
    // re-submitted stale/inappropriate data just because the admin never
    // touched a field they couldn't even see.
    const fieldValue = (key) => (isFieldVisible(key) ? Number(limits[key]) || 0 : 0);

    const roleUs = {
      customer: targetRoleName === "Royalty",
      seller: targetRoleName === "Seller",
      provider: targetRoleName === "Provider",
      userid: targetUserId,
      role: computeRoleHash(targetRoleName, targetUserId),
      [targetRoleName]: true,
      roleName: targetRoleName,
      level: config.level,
      maxRoyalite: fieldValue("maxRoyalite"),
      maxSeller: fieldValue("maxSeller"),
      maxProducer: fieldValue("maxProducer"),
      maxAssociate: fieldValue("maxAssociate"),
      maxInvestment: fieldValue("maxInvestment"),
      maxAdmin: fieldValue("maxAdmin"),
    };
    const userIn = {
      firstname: targetFirstName,
      lastname: targetLastName,
      emailUser: targetEmail,
      roleName: targetRoleName,
      maxRoyalite: roleUs.maxRoyalite,
      maxSeller: roleUs.maxSeller,
      maxProducer: roleUs.maxProducer,
      maxAdmin: roleUs.maxAdmin,
    };
    const myInfo = { password, nbrDays };

    // emailExit:false + emailvsTel:true -- the "this account already exists,
    // update its role" branch (never the "create a brand-new default
    // account" branch, which is what emailExit:true means server-side).
    const data = await UpdateUserRoleByAdmin(token, userIn, roleUs, myInfo, EMPTY_TEL, false, true);
    setSubmitting(false);

    if (data?.error) {
      setFormError(data.error);
    } else {
      setFormSuccess(__elakayText("t009", "Updated. Awaiting the user's acceptance."));
      onSuccess && onSuccess(data);
    }
  };

  return (
    <Modal show={show} onHide={onHide} dialogClassName="edit-role-capacity-modal" size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {__elakayText("t010", "Edit role limits")} — {targetFirstName} {targetLastName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!config && (
          <div className="edit-role-capacity-alert edit-role-capacity-alert-error">
            {__elakayText("t008", "This role can't be edited here.")}
          </div>
        )}

        {config && (
          <form onSubmit={handleSubmit} className="edit-role-capacity-form">
            {nbrDays > 0 && (
              <div className="edit-role-capacity-countdown" key={nbrDays}>
                <Timer infoSource={infoSource} nbrdays={nbrDays} enDate={expirationDate} />
              </div>
            )}

            <div className="edit-role-capacity-field">
              <label htmlFor="edit-role-capacity-days">
                {__elakayText("t011", "Enter the number day for expiration")}
              </label>
              <input
                id="edit-role-capacity-days"
                type="range"
                min="0"
                max="90"
                value={nbrDays}
                onChange={(e) => setNbrDays(Number(e.target.value))}
              />
              <span className="edit-role-capacity-days-value">{nbrDays}</span>
            </div>

            <div className="edit-role-capacity-field">
              <label>{__elakayText("t012", "His email address")}</label>
              <input type="email" value={targetEmail || ""} readOnly disabled />
              <small>{__elakayText("t013", "This is the account being edited -- not editable here.")}</small>
            </div>

            {/* Promote to a superior role -- only offered when the acting
                admin actually has at least one higher role tier they're
                allowed to grant (promotableRoles, computed by cardUser.jsx).
                Never lists the current role's own level or anything below
                it -- this picker only ever raises the role, it never
                demotes (use Suspend for taking access away). */}
            {promotableRoles.length > 0 && (
              <div className="edit-role-capacity-field">
                <label htmlFor="edit-role-capacity-role">{__elakayText("t021", "Role")}</label>
                <select
                  id="edit-role-capacity-role"
                  value={targetRoleName}
                  onChange={(e) => setTargetRoleName(e.target.value)}
                >
                  <option value={roleName}>
                    {(ROLE_LABELS[roleName] && __elakayText(ROLE_LABELS[roleName].key, ROLE_LABELS[roleName].fallback)) || roleName}
                    {" "}({__elakayText("t022", "current")})
                  </option>
                  {promotableRoles.map((rn) => (
                    <option key={rn} value={rn}>
                      {(ROLE_LABELS[rn] && __elakayText(ROLE_LABELS[rn].key, ROLE_LABELS[rn].fallback)) || rn}
                    </option>
                  ))}
                </select>
                {isPromoting && (
                  <small>
                    {__elakayText(
                      "t030",
                      "This proposes a promotion -- it only takes effect once the user accepts it."
                    )}
                  </small>
                )}
              </div>
            )}

            {visibleFields.map(({ key, labelKey, fallback }) => (
              <div className="edit-role-capacity-field" key={key}>
                <label htmlFor={`edit-role-capacity-${key}`}>{__elakayText(labelKey, fallback)}</label>
                <div className="edit-role-capacity-limit-row">
                  <input
                    id={`edit-role-capacity-${key}`}
                    type="number"
                    min="0"
                    value={limits[key]}
                    onChange={handleLimitChange(key)}
                  />
                  <button
                    type="button"
                    className="edit-role-capacity-zero-btn"
                    onClick={handleResetLimit(key)}
                    disabled={Number(limits[key]) === 0}
                    title={__elakayText("t019", "Not appropriate for this role? Set it to 0.")}
                  >
                    {__elakayText("t020", "Set to 0")}
                  </button>
                </div>
              </div>
            ))}

            <div className="edit-role-capacity-field">
              <label htmlFor="edit-role-capacity-password">{__elakayText("t014", "Password")}</label>
              <input
                id="edit-role-capacity-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <small>{__elakayText("t015", "Your password, to confirm it's you.")}</small>
            </div>

            <ErrorAlert
              error={formError}
              success={formSuccess}
              setError={setFormError}
              setSuccess={setFormSuccess}
              duration={10000}
            />

            <div className="edit-role-capacity-actions">
              <button type="button" className="edit-role-capacity-cancel" onClick={onHide}>
                {__elakayText("t016", "Cancel")}
              </button>
              <button type="submit" className="edit-role-capacity-submit" disabled={submitting || !isFormValid}>
                {submitting ? __elakayText("t017", "Saving…") : __elakayText("t018", "Save")}
              </button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditRoleCapacityModal;
